/** @jsxImportSource ./simboluri-jsx */
import { Fragment, useEffect, useRef } from "react";
import { StepState } from "./tipuri";
import { Focus } from "./legaturi";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import { Divergente, Eticheta, Legenda, MarkerDivergenta, Pas, TermenOmis } from "./Explica";
import TextSimboluri, { S, TextMetoda } from "./Simbol";
import ModeleRigiditate from "./ModeleRigiditate";
import { numar, t, tn, useT, tx } from "../i18n";
import { traduceRezultat } from "./texteRezultat";

// Pasul 10 — Deplasările laterale (driftul de nivel), P100-1/2013 Anexa E.
//
// Ce arată, în ordinea în care se citește:
//   1. parametrii care intră în relații (T₁, T_C, q, ν, c) — fiecare cu
//      formula lui substituită, ca la §E.1/E.2;
//   2. secțiunea de încastrare: de la ce nivel în sus se verifică driftul, cu
//      cele șase condiții și verdictul fiecăreia (C5.8.1);
//   3. verificările, cu maximul pe niveluri × puncte, nivelul și punctul care
//      guvernează, și deficitul „cu cât trebuie redus driftul";
//   4. tabelul pe niveluri, per direcție;
//   5. profilul amplificării pe verticală («efectul de bici»): rigiditatea de
//      nivel — cea de CADRU (verdictul), cea proprie și cea secantă, una
//      lângă alta —, masa și aria, fiecare cu raportul față de nivelul de
//      dedesubt — acolo se citește DE CE se biciuiește un nivel, nu doar că
//      o face;
//   6. divergențele față de Eurocod, marcate deja în formulele de sus.

function pct(x?: number | null, z = 3) {
  return x == null ? "—" : `${x.toFixed(z)} %`;
}

// Verdictul FIECĂREI direcții (driftul, rotirea de coardă, θ
// și efectul de bici se verifică pe fiecare direcție separat, iar Pasul 19
// primește câte o picată pe fiecare direcție deficitară). Rândul de sus rămâne
// maximul — coloana asta arată de unde vine și ce mai pică pe lângă el.
function PerDirectie({ v }: { v: any }) {
  const pd = (v.per_directie || []).filter(
    (d: any) => d && d.valoare_pct != null);
  if (pd.length === 0) {
    // rezultate salvate înainte de verdictele per direcție (și cutia rigidă,
    // care n-are direcție)
    if (v.directie == null) {
      const th = (v.per_directie || []).filter((d: any) => d?.theta_max != null);
      return th.length > 0 ? (
        <span className="sg-muted">
          {th.map((d: any) => `${d.directie}: ${d.theta_max}`).join(" · ")}
        </span>
      ) : <span className="sg-muted">—</span>;
    }
    return (
      <>{t("struct.dr.directia_nivelul", { dir: v.directie, niv: v.nivel })}
        <div className="sg-muted" style={{ fontSize: 11 }}>
          {t("struct.dr.punctul_cazul", { punct: traduceRezultat(v.punct), caz: v.caz })}
        </div>
      </>
    );
  }
  return (
    <>
      {pd.map((d: any) => (
        <div key={d.directie} style={{ fontSize: 12 }}>
          <span className={d.ok === false ? "st-bad" : "st-ok"}>
            {d.ok === false ? "✗" : "✓"}
          </span>{" "}
          {t("struct.dr.directia_val", { dir: d.directie })}{" "}
          <span className="sg-mono">{pct(d.valoare_pct, 3)}</span>
          {d.deficit_pct != null && (
            <span className="st-bad">
              {t("struct.dr.deficit_pct", { v: d.deficit_pct })}
            </span>
          )}
          <div className="sg-muted" style={{ fontSize: 11 }}>
            {d.nivel_eticheta
              || (d.nivel != null ? t("struct.dr.nivelul_n", { n: d.nivel }) : "")}
            {d.punct && t("struct.dr.virgula_punctul", { punct: traduceRezultat(d.punct) })}
            {d.caz && t("struct.dr.virgula_cazul", { caz: d.caz })}
          </div>
        </div>
      ))}
      {(v.directii_picate?.length ?? 0) > 1 && (
        <div className="sg-muted" style={{ fontSize: 11, marginTop: 2 }}>
          {t("struct.dr.pica_amandoua", {
            lista: v.directii_picate.join(t("struct.dr.si")), dir: v.directie,
          })}
        </div>
      )}
    </>
  );
}

// Pe CE MODEL s-a calculat driftul: beton întreg pentru perioade și forțe,
// E_c,ef pentru deplasări (P100-1 tabelul E.1 + §E.1(2)).
//
// Panoul e primul lucru de pe pagină, înaintea parametrilor, fiindcă e
// premisa tuturor cifrelor de sub el: același model, alt E, alt drift. Stefan
// a cerut explicit ca decizia să se vadă din interfață — o
// reducere de rigiditate care dublează driftul nu are voie să fie o
// preferință ascunsă în fișierul de configurare.
function Rigiditate({ rig }: { rig: any }) {
  useT();
  if (!rig) return null;
  const amp = rig.amplificare;
  const infra = rig.infrastructura;
  return (
    <div className="sg-card" style={{ marginBottom: 14 }}>
      <h3 style={{ marginTop: 0 }}>
        {t("struct.dr.rigiditatea_titlu")}
      </h3>
      <p style={{ fontSize: 13, maxWidth: 900, marginTop: 4 }}>
        <TextMetoda cheie="struct.dr.rigiditatea_text" />
        {rig.articol && (
          <TextMetoda cheie="struct.dr.ancora_normativa"
                      valori={{ articol: rig.articol }} />
        )}
      </p>
      {rig.pe_beton_intreg && (
        <p className="st-bad" style={{ fontSize: 12, maxWidth: 900 }}>
          <TextSimboluri text={rig.avertisment} />
        </p>
      )}
      {(rig.familii || []).length > 0 && (
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.dr.th_familia")}</th>
              <th>k = E/<S k="E_c">E_c</S></th>
              <th>{t("struct.dr.th_e_calcul")}</th>
              <th>{t("struct.dr.atat")}</th>
            </tr>
          </thead>
          <tbody>
            {rig.familii.map((f: any) => (
              <tr key={f.familie}>
                <td>{f.eticheta}</td>
                <td className="sg-mono">{f.factor}</td>
                <td className="sg-mono">
                  {f.E_ef_kpa == null ? "—"
                    : `${(f.E_ef_kpa / 1e6).toFixed(2)} GPa`}
                </td>
                <td style={{ fontSize: 12 }}>
                  <TextSimboluri text={f.motiv} />
                </td>
              </tr>
            ))}
            <tr>
              <td>{t("struct.dr.infrastructura_rand")}</td>
              <td className="sg-mono">{infra?.nefisurata ? numar(1, { minimumFractionDigits: 1 }) : t("struct.dr.ca_mai_sus")}</td>
              <td className="sg-mono">
                {infra?.nefisurata && rig.E_c_kpa != null
                  ? `${(rig.E_c_kpa / 1e6).toFixed(2)} GPa`
                  : "—"}
              </td>
              <td style={{ fontSize: 12 }}>
                <TextSimboluri text={infra?.motiv} />
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {amp?.raport != null && (
        <p style={{ fontSize: 13, marginTop: 8, maxWidth: 900 }}>
          <strong>{t("struct.dr.costa_fisurarea_masurat")}</strong>{" "}
          <TextSimboluri text={amp.explicatie} />
          {t("struct.dr.cifra_raspunde")}
        </p>
      )}
      {(rig.T1_drift_s ?? rig.T1_ec_ef_s) && (
        <p className="sg-muted" style={{ fontSize: 12 }}>
          <TextMetoda cheie="struct.dr.perioada_fundamentala" valori={{
            x: (rig.T1_drift_s ?? rig.T1_ec_ef_s).X,
            y: (rig.T1_drift_s ?? rig.T1_ec_ef_s).Y,
          }} />
        </p>
      )}
    </div>
  );
}

function Verdicte({ verificari, focus }: { verificari: any[]; focus?: Focus }) {
  useT();
  // aceleași titluri, același hover și același stil ca la Pașii 13–16
  const catalog = useCatalogVerificari();
  // driftul nu are ELEMENT de selectat: verdictele lui sunt ale ansamblului.
  // Linkul dintr-o verificare picată a Pasului 19 poartă id-ul verificării
  // (`drift_sls`, `cutie_rigida`…), iar aici se derulează la rândul ei și se
  // aprinde — altfel ajungeai pe pagină fără să știi care din cele cinci.
  const tabel = useRef<HTMLTableElement>(null);
  useEffect(() => {
    if (!focus?.ref) return;
    const rand = tabel.current?.querySelector(`[data-ver="${CSS.escape(focus.ref)}"]`);
    rand?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focus?.n, focus?.ref]);
  return (
    <table className="sg-table ver-tabel" ref={tabel}>
      <thead>
        <tr>
          <th>{t("struct.dr.th_verificarea")}</th>
          <th>{t("struct.dr.th_valoare")}</th>
          <th>{t("verificari.col_prag")}</th>
          <th>{t("struct.dr.unde_guverneaza")}</th>
          <th>{t("struct.tipver.col_deficit")}</th>
          <th>{t("struct.inc.col_articol")}</th>
        </tr>
      </thead>
      <tbody>
        {verificari.map((v) => (
          <tr key={v.id} data-ver={v.id}
              className={focus?.ref === v.id ? "ver-focus" : undefined}>
            <td>
              {v.ok === null
                ? <span className="sg-muted">○</span>
                : <span className={v.ok ? "st-ok" : "st-bad"}>{v.ok ? "✓" : "✗"}</span>}
              {" "}
              <TitluVerificare catalog={catalog} pas="deplasari_laterale"
                               id={v.id} nume={v.nume} aratPasul={false}
                               aratNumele={false} />
              {v.formula && (
                <div className="sg-muted" style={{ fontSize: 11, marginTop: 3 }}>
                  {v.formula}
                </div>
              )}
              {v.ok === null && v.nota && (
                <div className="sg-muted" style={{ fontSize: 11, marginTop: 3 }}>
                  {t("struct.dr.nu_se_cere", { nota: traduceRezultat(v.nota) })}
                </div>
              )}

            </td>
            <td className="sg-mono">{pct(v.valoare_pct)}</td>
            <td className="sg-mono">{pct(v.prag_pct, 2)}</td>
            <td><PerDirectie v={v} /></td>
            <td className="sg-mono">
              {v.deficit_pct != null
                ? <span className="st-bad">−{v.deficit_pct} %</span>
                : <span className="sg-muted">—</span>}
            </td>
            <td style={{ fontSize: 11 }}>{v.articol}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Pe ce bază stă modelul din care s-a citit d_re — încastrată (cutia
 *  rigidă urmărită) sau pe teren — și de ce (`surse.baza_drift`, scrisă de
 *  Pasul 10 din `rigiditate.baza_driftului`). Omul trebuie să vadă varianta
 *  folosită lângă cifrele de drift, nu s-o deducă. */
function BazaDrift({ b }: { b: any }) {
  useT();
  if (!b || !b.baza) return null;
  const fortat = b.mod && b.mod !== "auto";
  return (
    <div className="st-findings" style={{ marginTop: 12 }}>
      <h3>
        {t("struct.dr.baza_drift_titlu")}
        {" "}<Eticheta tip={fortat ? "intrare" : "decizie"} />
      </h3>
      <p className="sg-muted ex-intro">
        <strong>{t(b.baza === "incastrat" ? "struct.dr.baza_incastrata" : "struct.dr.baza_elastica")}</strong>
        {" — "}
        {fortat ? t("struct.dr.baza_mod_fortat", { mod: String(b.mod) }) : t("struct.dr.baza_mod_auto")}
        {b.motiv && <>: <TextSimboluri text={String(b.motiv)} /></>}
      </p>
    </div>
  );
}

function Incastrare({ inc }: { inc: any }) {
  useT();
  if (!inc) return null;
  return (
    <div className="st-findings" style={{ marginTop: 12 }}>
      <h3>
        {t("struct.dr.sectiunea_incastrare", { n: inc.nivel })}
        {inc.cota_m != null && (
          <span className="sg-muted">{t("struct.dr.cota_m", { v: inc.cota_m })}</span>
        )}
        {" "}<Eticheta tip={inc.mod === "auto" ? "decizie" : "intrare"} />
      </h3>
      <p className="sg-muted ex-intro">
        <TextMetoda cheie="struct.dr.incastrare_text"
                    valori={{ motiv: inc.motiv, articol: inc.articol }} />
      </p>
      {inc.raport_rigiditate_masurat != null && (
        <p style={{ fontSize: 13 }}>
          <TextMetoda cheie="struct.dr.raport_rigiditate"
                      valori={{ v: inc.raport_rigiditate_masurat }} />
          <span className="sg-muted">{t("struct.dr.pe_modelul_elastic")}</span>
        </p>
      )}
      <table className="sg-table">
        <thead>
          <tr><th>#</th><th>{t("struct.dr.conditia")}</th>
              <th>{t("struct.dr.th_valoare")}</th>
              <th>{t("struct.dr.th_sursa")}</th></tr>
        </thead>
        <tbody>
          {(inc.conditii || []).map((c: any) => (
            <tr key={c.id}>
              <td className="sg-mono">
                {c.ok === true ? <span className="st-ok">✓</span>
                  : c.ok === false ? <span className="st-bad">✗</span>
                  : <span className="sg-muted">○</span>}
                {" "}{c.id}
              </td>
              <td>
                {c.nume}
                {c.formula && (
                  <div className="sg-muted" style={{ fontSize: 11 }}>{c.formula}</div>
                )}
                {c.nota && (
                  <div className="sg-muted" style={{ fontSize: 11 }}>{c.nota}</div>
                )}
                {/* cum e REPREZENTAT în model ce se verifică aici: condiția
                    I2m a fost o dată victima modelului (panourile de subsol
                    erau bare, deci grosimea nu putea mișca raportul), așa că
                    reprezentarea se scrie lângă verdict */}
                {c.sursa_model_panou && (
                  <div className="sg-muted" style={{ fontSize: 11 }}>
                    {c.sursa_model_panou}
                  </div>
                )}
              </td>
              <td className="sg-mono">{c.valoare}</td>
              <td style={{ fontSize: 11 }}>{c.sursa}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Profilul amplificării pe verticală: rigiditatea de nivel — trei mărimi
 *  una lângă alta, dintre care UNA decide —, masa și aria, fiecare cu raportul
 *  față de nivelul de dedesubt. Verdictul unui rând vine NUMAI din rigiditatea
 *  de CADRU (§4.4.3.3(2)); retragerea și masa se arată pentru că explică de
 *  unde vine saltul, dar nu pică singure — de aceea un rând poate fi verde și
 *  să aibă totuși o observație dedesubt.
 *
 *  Rezultatele salvate înainte de rigiditatea de cadru au doar `k_lat_kn_mm`
 *  și `raport_k`: coloanele lipsă rămân «—», verdictul rămâne pe ce există. */
function Amplificare({ amplificare }: { amplificare: any[] }) {
  useT();
  if (!amplificare || amplificare.length === 0) return null;
  const nr = (x: any, z = 3) => (x == null ? "—" : Number(x).toFixed(z));
  const kn = (x: any) => (x == null ? "—" : `${nr(x, 1)} kN/mm`);
  return (
    <>
      <h3 style={{ marginTop: 16 }}>
        {t("struct.dr.amplificare_titlu")}
      </h3>
      {/* Pe ecran sunt DOUĂ modele (beton întreg pentru forțe, E_c,ef pentru
          deplasări), deci fiecare cifră trebuie să-și spună modelul — altfel
          două numere de pe aceeași pagină par comparabile și nu sunt.
          Decizia: biciul rămâne pe beton întreg. */}
      <p style={{ fontSize: 13, maxWidth: 900, marginTop: 4 }}>
        <TextMetoda cheie="struct.dr.muto_text" />
      </p>
      <p style={{ fontSize: 13, maxWidth: 900, marginTop: 4 }}>
        <TextMetoda cheie="struct.dr.coloane_informative" />
      </p>
      <p style={{ fontSize: 13, maxWidth: 900, marginTop: 4 }}>
        <TextMetoda cheie="struct.dr.beton_intreg_text" />
      </p>
      <Legenda
        coloane={[
          { nume: t("struct.dr.k_cadru"), ce: t("struct.dr.rigiditatea_laterala_cad"), formula: t("struct.dr.f_k_cadru"), sursa: t("struct.dr.geometria_modelului_pasu"), validare: t("struct.dr.v_k_cadru") },
          { nume: "K_i/K_i−1", ce: t("struct.dr.ramane_rigiditatea_cadru"), formula: t("struct.dr.f_raport_cadru"), validare: t("struct.dr.v_raport_cadru") },
          { nume: t("struct.dr.k_proprie"), ce: t("struct.dr.rigiditatea_stalpilor_si"), formula: t("struct.dr.f_k_proprie"), validare: t("struct.dr.v_k_proprie") },
          { nume: t("struct.dr.k_secanta"), ce: t("struct.dr.rigiditatea_sistemului_v"), formula: t("struct.dr.f_k_secanta"), sursa: t("struct.dr.v_pasul_9"), validare: t("struct.dr.v_k_secanta") },
          { nume: t("struct.dr.masa"), ce: t("struct.dr.masa_seismica_a"), formula: t("struct.dr.f_masa"), sursa: t("struct.dr.pasul_7") },
          { nume: "a", ce: t("struct.dr.acceleratia_nivel_biciui"), formula: t("struct.dr.f_acceleratie"), validare: t("struct.dr.v_acceleratie") },
          { nume: t("struct.dr.retragere"), ce: t("struct.dr.retrage_nivelul_plan"), formula: t("struct.dr.f_retragere"), validare: "≤ 20 % (§4.4.3.3(1))" },
          { nume: t("struct.dr.stalpi"), ce: t("struct.dr.cati_stalpi_are"), formula: t("struct.dr.f_stalpi"), validare: t("struct.rzv.informativ") },
        ]}
      />
      {amplificare.map((d: any) => (
        <div key={d.directie} style={{ marginTop: 10 }}>
          <h4 style={{ margin: "6px 0" }}>
            {t("struct.arpl.directia_dir", { dir: d.directie })}
            <span className="sg-muted" style={{ fontWeight: 400 }}>
              {" "}{t("struct.dr.raport_minim", { v: nr(d.raport_k_min) })}
              {d.intre_critic ? <> {d.intre_critic}</>
                : d.nume_critic ? <> {t("struct.dr.la_nume", { nume: traduceRezultat(d.nume_critic) })}</> : null}
              {d.amplificare_max != null && (
                <> {t("struct.dr.amplificare_max", { v: nr(d.amplificare_max, 2) })}</>
              )}
            </span>
          </h4>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.top.nivel")}</th>
                <th title={t("struct.dr.rigiditatea_cadru_d")}>{t("struct.dr.k_cadru")}</th>
                <th title={t("struct.dr.raportul_cadru_fata")}>K_i/K_i−1</th>
                <th className="sg-muted" title={t("struct.dr.12ei_h3_stalpi")}>{t("struct.dr.k_proprie")}</th>
                <th className="sg-muted" title={t("struct.dr.raportul_rigiditatilor_p")}>{t("struct.dr.rap")}</th>
                <th className="sg-muted" title={t("struct.dr.titlu_k_secanta")}>{t("struct.dr.k_secanta")}</th>
                <th className="sg-muted" title={t("struct.dr.raportul_rigiditatilor_s")}>{t("struct.dr.rap")}</th>
                <th>{t("struct.dr.masa")}</th><th>m_i/m_i−1</th>
                <th>a</th><th>a_i/a_i−1</th>
                <th>{t("struct.dr.arie")}</th><th>{t("struct.dr.retragere")}</th>
                <th title={t("struct.dr.stalpii_nivelului_parant")}>{t("struct.dr.stalpi")}</th>
              </tr>
            </thead>
            <tbody>
              {(d.niveluri || []).map((n: any) => (
                <Fragment key={n.nivel}>
                  <tr className={!n.suprastructura ? "st-infra" : undefined}>
                    <td>
                      {n.nivel} {n.nume && <span className="sg-muted">{n.nume}</span>}
                      {!n.suprastructura && (
                        <span className="sg-muted"
                              title={t("struct.dr.nivel_infrastructura_sal")}>
                          {" "}{t("struct.dr.infrastructura")}
                        </span>
                      )}
                    </td>
                    <td className="sg-mono"><strong>{kn(n.k_lat_kn_mm)}</strong></td>
                    <td className={`sg-mono ${n.ok === false ? "st-bad" : ""}`}
                        title={n.intre ? t("struct.dr.raportul_masoara_p1", { p1: n.intre }) : undefined}>
                      <strong>{nr(n.raport_k)}</strong>
                      {n.nume_jos && (
                        <span className="sg-muted"> /{n.nume_jos}</span>
                      )}
                    </td>
                    <td className="sg-mono sg-muted">{kn(n.k_proprie_kn_mm)}</td>
                    <td className="sg-mono sg-muted">{nr(n.raport_k_proprie)}</td>
                    <td className="sg-mono sg-muted">{kn(n.k_secant_kn_mm)}</td>
                    <td className="sg-mono sg-muted">{nr(n.raport_k_secant)}</td>
                    <td className="sg-mono">{nr(n.masa_t, 1)} t</td>
                    <td className="sg-mono sg-muted">{nr(n.raport_masa)}</td>
                    <td className="sg-mono">{nr(n.a_nivel_ms2, 2)} m/s²</td>
                    <td className="sg-mono">{nr(n.raport_acceleratie, 2)}</td>
                    <td className="sg-mono sg-muted">{nr(n.arie_m2, 1)} m²</td>
                    <td className="sg-mono">
                      {n.retragere_pct == null ? "—" : `${nr(n.retragere_pct, 1)} %`}
                    </td>
                    <td className="sg-mono sg-muted">
                      {n.nr_stalpi ?? "—"}
                      {n.nr_stalpi_articulati > 0 && (
                        <span title={t("struct.dr.stalpi_baza_articulata")}>
                          {" "}{tn("struct.dr.articulati#n", n.nr_stalpi_articulati)}
                        </span>
                      )}
                    </td>
                  </tr>
                  {(n.motive || []).length > 0 && (
                    <tr>
                      <td colSpan={14} className="sg-muted"
                          style={{ fontSize: 11, paddingLeft: 18 }}>
                        {n.ok === false ? "✗ " : "○ "}
                        {n.intre ? `${n.intre}: ` : ""}
                        {(n.motive || []).join(" · ")}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

export default function StepDrift({ state, focus }: {
  state: StepState | null;
  // verificarea cerută dintr-un link (?focus=cutie_rigida) — v. legaturi.ts
  focus?: Focus;
}) {
  useT();
  if (!state) return null;
  if (state.status === "error") {
    return <div className="st-error">{(state.result as any)?.error}</div>;
  }
  const r: any = state.result;
  if (!r) {
    return (
      <Pas titlu={t("struct.dr.pasul_10_deplasari")}>
        {t("struct.dr.verifica_deplasarea_rela")}
      </Pas>
    );
  }
  const p = r.parametri || {};
  const theta = r.theta || [];

  return (
    <div>
      <Pas titlu={t("struct.dr.pasul_10_deplasari2")}>
        <TextMetoda cheie="struct.dr.intro" />
      </Pas>

      <ModeleRigiditate pas={10} r={r} />
      <Rigiditate rig={r.rigiditate} />

      <h3>{t("struct.dr.parametrii_intra_relatii")}</h3>
      <table className="sg-table">
        <tbody>
          <tr>
            <td><S k="T1">T₁</S> <Eticheta tip="masurat" /></td>
            <td className="sg-mono">
              {p.T1_pe_directie_s
                ? <>X: {p.T1_pe_directie_s.X} s<br />Y: {p.T1_pe_directie_s.Y} s</>
                : <>{p.T1_s} s</>}
            </td>
            <td>
              <TextMetoda cheie="struct.dr.t1_directie" />
              {p.T1_mod_pe_directie && (
                <span className="sg-muted">
                  {" "}{t("struct.dr.t1_moduri", {
                    x: p.T1_mod_pe_directie.X, y: p.T1_mod_pe_directie.Y,
                  })}
                </span>
              )}
              <TextMetoda cheie="struct.dr.t1_litera" valori={{ t1: p.T1_s }} />
            </td>
          </tr>
          <tr>
            <td><S k="Tc">T_C</S> <Eticheta tip="intrare" /></td>
            <td className="sg-mono">{p.T_C_s} s</td>
            <td>{tx("struct.dr.perioada_control_a")}</td>
          </tr>
          <tr>
            <td><S k="q">q</S> <Eticheta tip="intrare" /></td>
            <td className="sg-mono">{p.q}</td>
            <td>{t("struct.dr.q_text")}</td>
          </tr>
          <tr>
            <td><S k="nu_sls">ν</S> <Eticheta tip="intrare" /></td>
            <td className="sg-mono">{p.nu_sls}</td>
            <td>
              <TextMetoda cheie="struct.dr.nu_text" />{" "}
              <TermenOmis cod="E2" alternativa={t("struct.dr.nu_alternativa")}>
                {t("struct.dr.0_4_clase")}
              </TermenOmis>
            </td>
          </tr>
          <tr>
            <td>c <Eticheta tip="masurat" /></td>
            <td className="sg-mono">
              {p.c_pe_directie
                ? <>X: {p.c_pe_directie.X}<br />Y: {p.c_pe_directie.Y}</>
                : <>{p.c_deplasare}</>}
            </td>
            <td>
              {p.c_explicatie_pe_directie
                ? (["X", "Y"] as const).map((d) => (
                    <div key={d} style={{ marginBottom: 3 }}>
                      <TextSimboluri text={p.c_explicatie_pe_directie[d]} />
                    </div>
                  ))
                : <TextSimboluri text={p.c_explicatie} />}
              <div className="sg-muted" style={{ fontSize: 11, marginTop: 3 }}>
                {t("struct.dr.c_propriu")} <MarkerDivergenta cod="E1" />
              </div>
            </td>
          </tr>
          <tr>
            <td>{t("struct.dr.limita_sls")} <Eticheta tip="decizie" /></td>
            <td className="sg-mono">{p.limita_sls_pct} %·h</td>
            <td>
              {t("struct.dr.tabelul_e2", { cns: p.eticheta_cns })}
              <div className="sg-muted" style={{ fontSize: 11 }}>
                {/* decizia Pasului 3: de unde vine clasa (preferință,
                    arhitectură, estimare LLM, implicit) și de ce */}
                {p.cns && <>{p.cns.eticheta_sursa}: {p.cns.motiv}</>}
                {p.fatada_agatata && t("struct.dr.fatada_agatata")}
              </div>
              {/* o suprascriere salvată după Pasul 3 nu intră până nu se
                  rulează din nou pasul — se spune */}
              {p.avertisment_preferinte && (
                <div className="sg-muted" style={{ fontSize: 11, marginTop: 3, fontWeight: 600 }}>
                  {p.avertisment_preferinte}
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td>{t("struct.dr.limita_uls")} <Eticheta tip="intrare" /></td>
            <td className="sg-mono">{p.limita_uls_pct} %·h</td>
            <td>
              {t("struct.dr.uls_text")} <MarkerDivergenta cod="E3" />
            </td>
          </tr>
        </tbody>
      </table>

      <h3 style={{ marginTop: 16 }}>{t("struct.dr.cererea_deplasare")}</h3>
      <p style={{ fontSize: 13, maxWidth: 860 }}>
        <TextMetoda cheie="struct.dr.cerere_text" />
      </p>
      <p className="ex-formula" style={{ fontSize: 13 }}>
        d_re,X = |E_0| + |T_accX|{" "}
        {!p.regula_30 && (
          <TermenOmis
            cod="E5"
            alternativa={
              p.drift_sls_max_regula30_pct != null
                ? t("struct.dr.termen_neaplicat_metoda", { p1: p.drift_sls_max_regula30_pct, p2: p.drift_sls_max_pct })
                : t("struct.dr.termen_neaplicat")
            }
          >
            + 0,3·|E_90|
          </TermenOmis>
        )}
        <br />
        d_r<sup>SLS</sup> = ν·q·d_re = {p.nu_sls}·{p.q}·d_re
        {" "}≤ {p.limita_sls_pct} %·h
        <br />
        d_r<sup>ULS</sup> = c·q·d_re ={" "}
        {p.c_pe_directie
          ? t("struct.dr.p1_p2_d", { p1: p.c_pe_directie.X, p2: p.q, p3: p.c_pe_directie.Y, p4: p.q })
          : `${p.c_deplasare}·${p.q}·d_re`}
        {" "}≤ {p.limita_uls_pct} %·h
        {" "}
        <span className="sg-muted" style={{ fontSize: 11 }}>
          {t("struct.dr.drift_total")} <MarkerDivergenta cod="E6" />
          {p.drift_sls_max_distorsiune_pct != null && (
            <>{t("struct.dr.in_loc_de_sls", {
              a: p.drift_sls_max_distorsiune_pct, b: p.drift_sls_max_pct,
            })}</>
          )}
        </span>
      </p>

      <h3 style={{ marginTop: 16 }}>{t("struct.dr.verificarile")}</h3>
      <Verdicte verificari={r.verificari || []} focus={focus} />

      <BazaDrift b={r.surse?.baza_drift} />
      <Incastrare inc={r.sectiune_incastrare} />

      <h3 style={{ marginTop: 16 }}>{t("struct.dr.niveluri")}</h3>
      <Legenda
        coloane={[
          { nume: "d_re", ce: t("struct.dr.deplasarea_relativa_elas"), formula: t("struct.dr.f_d_re"), sursa: t("struct.dr.blocul_dr_tensorului") },
          { nume: t("struct.arp.th_punct"), ce: t("struct.dr.punctul_plan_s"), formula: t("struct.dr.f_punct"), validare: t("struct.rzv.informativ") },
          { nume: "drift SLS", ce: t("struct.dr.deplasarea_relativa_star"), formula: "ν·q·d_re/h", validare: t("struct.dr.v_sls", { v: p.limita_sls_pct }) },
          { nume: "drift ULS", ce: t("struct.dr.idem_starea_limita"), formula: "c·q·d_re/h", validare: t("struct.dr.v_uls", { v: p.limita_uls_pct }) },
          { nume: t("struct.dr.distorsiune"), ce: t("struct.dr.driftul_sls_s"), formula: t("struct.dr.f_distorsiune"), validare: t("struct.dr.v_distorsiune") },
          { nume: "θ", ce: t("struct.dr.coeficientul_sensibilita"), formula: "P_tot·d_r^ULS/(V_tot·h)", validare: t("struct.dr.v_theta") },
        ]}
      />
      {(r.directii || []).map((d: any) => {
        const th = theta.find((t: any) => t.directie === d.directie);
        return (
          <div key={d.directie} style={{ marginTop: 10 }}>
            <h4 style={{ margin: "6px 0" }}>
              {t("struct.arpl.directia_dir", { dir: d.directie })}
              <span className="sg-muted" style={{ fontWeight: 400 }}>
                {d.T1_directie_s != null && (
                  <> — T₁ = {d.T1_directie_s} s
                    {d.T1_mod != null && <> {t("struct.dr.modul_n", { n: d.T1_mod })}</>}
                    {d.c != null && <>, c = {d.c}</>}</>
                )}
                {" "}· θ_max = {th?.theta_max} → α = {th?.alpha}
                {" "}{t("struct.dr.se_transmite_11")}
              </span>
            </h4>
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.top.nivel")}</th><th>h</th><th>d_re</th><th>{t("struct.arp.th_punct")}</th>
                  <th>d_r SLS</th><th>drift SLS</th>
                  <th>d_r ULS</th><th>drift ULS</th>
                  <th>{t("struct.dr.distorsiune")}</th><th>θ</th>
                </tr>
              </thead>
              <tbody>
                {(d.niveluri || []).map((n: any) => {
                  // NU «t»: ar umbri funcția de traducere folosită două rânduri
                  // mai jos, iar pagina pica întreagă («t is not a function»)
                  const thN = (th?.niveluri || []).find((x: any) => x.nivel === n.nivel);
                  const picaSls = n.drift_sls_pct > p.limita_sls_pct;
                  return (
                    <tr key={n.nivel} className={n.infrastructura ? "st-infra" : undefined}>
                      <td>
                        {n.nivel}
                        {n.infrastructura && (
                          <span className="sg-muted" title={n.nota}> {t("struct.dr.infrastructura")}</span>
                        )}
                      </td>
                      <td className="sg-mono">{n.h_m} m</td>
                      <td className="sg-mono">{n.d_re_mm} mm</td>
                      <td className="sg-mono">{n.punct}</td>
                      <td className="sg-mono">{n.d_r_sls_mm} mm</td>
                      <td className={`sg-mono ${!n.infrastructura && picaSls ? "st-bad" : ""}`}>
                        {n.drift_sls_pct} %
                      </td>
                      <td className="sg-mono">{n.d_r_uls_mm} mm</td>
                      <td className="sg-mono">{n.drift_uls_pct} %</td>
                      <td className="sg-mono sg-muted">
                        {n.drift_sls_distorsiune_pct} %
                      </td>
                      <td className="sg-mono">{thN?.theta ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {th?.sursa_forfecare && (
              <div className="sg-muted" style={{ fontSize: 11, marginTop: 3 }}>
                {t("struct.dr.v_tot_theta", { sursa: th.sursa_forfecare })}
              </div>
            )}
          </div>
        );
      })}

      <Amplificare amplificare={r.amplificare} />

      <Divergente divergente={r.divergente} />

      {r.nota && (
        <p className="sg-muted" style={{ marginTop: 12, maxWidth: 820 }}>
          {r.nota}
        </p>
      )}
    </div>
  );
}
